import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Button,
  Card,
  Chip,
  IconButton,
  InputBase,
  MenuItem,
  Select,
  Typography,
} from "@mui/material";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import AutoAwesomeOutlinedIcon from "@mui/icons-material/AutoAwesomeOutlined";
import SearchRoundedIcon from "@mui/icons-material/SearchRounded";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import DeleteOutlineOutlinedIcon from "@mui/icons-material/DeleteOutlineOutlined";
import styles from "./zeroTrustPolicies.module.scss";
import ZeroTrustKpiCard from "./ZeroTrustKpiCard";
import AiPolicyAssistantDialog from "./AiPolicyAssistantDialog";
import CreatePolicyDialog from "./CreatePolicyDialog";
import EditPolicyDialog from "./EditPolicyDialog";
import ConfirmationDialog from "components/shared/Dialog/ConfirmationDialog";

const cards = [
  {
    label: "Active Policies",
    value: 4,
    accent: "#0FA3B1",
    tone: "#0f766e",
  },
  { label: "In Simulation", value: 1, accent: "#F4A261", tone: "#9a5c00" },
  { label: "Testing", value: 1, accent: "#2D8CFF", tone: "#0f52ba" },
  { label: "Total Rules", value: 65, accent: "#9CA3AF", tone: "#374151" },
];

const policyRows = [
  {
    id: "block-external-ot-access",
    name: "Block External OT Access",
    description:
      "This policy prevents any external network access to operational technology devices and systems.",
    type: "Denial",
    category: "Authentication",
    scope: "OT Network",
    status: "Active",
    enforcement: "Automatic",
    priority: "High",
    owner: "Security Team",
    rules: 12,
    created: "2024-11-15",
    phase: "Testing",
  },
  {
    id: "require-mfa-scada",
    name: "Require MFA for SCADA",
    description: "Enforces multi-factor authentication for SCADA systems.",
    type: "Authentication",
    category: "Authentication",
    scope: "SCADA Systems",
    status: "Active",
    enforcement: "Automatic",
    priority: "High",
    owner: "SOC Team",
    rules: 8,
    created: "2024-10-22",
    phase: "Testing",
  },
  {
    id: "iot-device-quarantine",
    name: "IoT Device Quarantine",
    description: "Isolates IoT sensors with anomalous behavior patterns.",
    type: "Containment",
    category: "Containment",
    scope: "IoT Sensors",
    status: "Simulation",
    enforcement: "Manual",
    priority: "Medium",
    owner: "OT Security",
    rules: 5,
    created: "2024-12-01",
    phase: "Simulation",
  },
  {
    id: "restrict-third-party-remote-access",
    name: "Restrict Third-Party Remote Access",
    description: "Limits vendor access to approved windows and endpoints.",
    type: "Access Control",
    category: "Access",
    scope: "Corporate WAN",
    status: "Active",
    enforcement: "Automatic",
    priority: "High",
    owner: "Security Team",
    rules: 10,
    created: "2024-09-10",
    phase: "Testing",
  },
];

const statusClass = {
  Active: styles.statusActive,
  Simulation: styles.statusSimulation,
  Testing: styles.statusTesting,
};

const typeTone = {
  Denial: styles.typeCritical,
  Authentication: styles.typeInfo,
  Containment: styles.typeWarning,
  "Access Control": styles.typeNeutral,
};

const ZeroTrustPolicies = () => {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState("");
  const [category, setCategory] = useState("All");
  const [phase, setPhase] = useState("All");
  const [assistantOpen, setAssistantOpen] = useState(false);
  const [assistantPrompt, setAssistantPrompt] = useState("");
  const [showGenerated, setShowGenerated] = useState(false);
  const [createPolicyOpen, setCreatePolicyOpen] = useState(false);
  const [editPolicyOpen, setEditPolicyOpen] = useState(false);
  const [selectedPolicy, setSelectedPolicy] = useState(null);
  const [policies, setPolicies] = useState(policyRows);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [policyToDelete, setPolicyToDelete] = useState(null);

  const handleDeleteOpen = (policy) => {
    setPolicyToDelete(policy);
    setDeleteOpen(true);
  };

  const handleDeleteClose = () => {
    setDeleteOpen(false);
    setPolicyToDelete(null);
  };

  const handleDeleteConfirm = () => {
    if (!policyToDelete) {
      handleDeleteClose();
      return;
    }
    setPolicies((prev) => prev.filter((policy) => policy.id !== policyToDelete.id));
    handleDeleteClose();
  };

  const filteredPolicies = useMemo(() => {
    return policies.filter((policy) => {
      const matchesSearch = policy.name
        .toLowerCase()
        .includes(searchTerm.toLowerCase());
      const matchesCategory =
        category === "All" || policy.category === category;
      const matchesPhase = phase === "All" || policy.phase === phase;
      return matchesSearch && matchesCategory && matchesPhase;
    });
  }, [category, phase, policies, searchTerm]);

  return (
    <Box className={styles.page}>
      <Box className={styles.header}>
        <div className={styles.titleBlock}>
          <Typography className={styles.title} fontWeight={600}>
            Zero Trust Policy Management
          </Typography>
          <Typography className={styles.subtitle}>
            Create, simulate, and enforce security policies
          </Typography>
        </div>
        <Box className={styles.headerActions}>
          <Button
            variant="outlined"
            startIcon={<AutoAwesomeOutlinedIcon />}
            className={styles.aiButton}
            onClick={() => setAssistantOpen(true)}
          >
            AI Policy Assistant
          </Button>
          <Button
            variant="contained"
            startIcon={<AddRoundedIcon />}
            className={styles.primaryButton}
            onClick={() => setCreatePolicyOpen(true)}
          >
            Create Policy
          </Button>
        </Box>
      </Box>

      <Box className={styles.statGrid}>
        {cards.map((card) => (
          <ZeroTrustKpiCard
            key={card.label}
            label={card.label}
            value={card.value}
            accent={card.accent}
            tone={card.tone}
            showBar
          />
        ))}
      </Box>

      <Card className={styles.filterCard}>
        <div className={styles.filterRow}>
          <div className={styles.searchBox}>
            <SearchRoundedIcon fontSize="small" />
            <InputBase
              placeholder="Search policies..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className={styles.searchInput}
              inputProps={{ "aria-label": "Search policies" }}
            />
          </div>
          <Select
            value={category}
            onChange={(e) => setCategory(e.target.value)}
            className={styles.select}
            variant="outlined"
            size="small"
          >
            {["All", "Authentication", "Access", "Containment"].map((opt) => (
              <MenuItem value={opt} key={opt}>
                {opt === "All" ? "All Types" : opt}
              </MenuItem>
            ))}
          </Select>
          <Select
            value={phase}
            onChange={(e) => setPhase(e.target.value)}
            className={styles.select}
            variant="outlined"
            size="small"
          >
            {["All", "Testing", "Simulation"].map((opt) => (
              <MenuItem value={opt} key={opt}>
                {opt === "All" ? "All Stages" : opt}
              </MenuItem>
            ))}
          </Select>
        </div>
      </Card>

      <Card className={styles.tableCard}>
        <div className={styles.tableHeader}>
          <Typography className={styles.tableTitle} fontWeight={700}>
            Active Policies
          </Typography>
          <Typography className={styles.tableHint}>
            Filtered view based on your selections
          </Typography>
        </div>
        <table className={styles.table}>
          <thead>
            <tr>
              <th>Policy Name</th>
              <th>Type</th>
              <th>Scope</th>
              <th>Status</th>
              <th>Enforcement</th>
              <th>Rules</th>
              <th>Created</th>
              <th className={styles.actionsHead}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredPolicies.map((policy) => (
              <tr
                key={policy.id}
                className={styles.tableRow}
                onClick={() => navigate(`/zero-trust-policies/${policy.id}`)}
              >
                <td>
                  <div className={styles.policyName}>{policy.name}</div>
                </td>
                <td>
                  <Chip
                    label={policy.type}
                    className={`${styles.typeChip} ${
                      typeTone[policy.type] || ""
                    }`}
                    size="small"
                  />
                </td>
                <td className={styles.scope}>{policy.scope}</td>
                <td>
                  <span
                    className={`${styles.status} ${
                      statusClass[policy.status] || ""
                    }`}
                  >
                    <span className={styles.statusDot} />
                    {policy.status}
                  </span>
                </td>
                <td className={styles.enforcement}>{policy.enforcement}</td>
                <td className={styles.rules}>{policy.rules}</td>
                <td className={styles.created}>{policy.created}</td>
                <td className={styles.actions}>
                  <IconButton
                    size="small"
                    className={styles.editButton}
                    onClick={(event) => {
                      event.stopPropagation();
                      setSelectedPolicy(policy);
                      setEditPolicyOpen(true);
                    }}
                  >
                    <EditOutlinedIcon />
                  </IconButton>
                  <IconButton
                    size="small"
                    className={styles.deleteButton}
                    onClick={(event) => {
                      event.stopPropagation();
                      handleDeleteOpen(policy);
                    }}
                  >
                    <DeleteOutlineOutlinedIcon />
                  </IconButton>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {filteredPolicies.length === 0 ? (
          <div className={styles.emptyState}>
            <Typography className={styles.emptyTitle}>
              No policies match these filters
            </Typography>
            <Typography className={styles.emptySub}>
              Adjust search keywords or stage to see more results.
            </Typography>
          </div>
        ) : null}
      </Card>

      <AiPolicyAssistantDialog
        open={assistantOpen}
        prompt={assistantPrompt}
        showGenerated={showGenerated}
        onPromptChange={(value) => setAssistantPrompt(value)}
        onSelectPrompt={(value) => setAssistantPrompt(value)}
        onGenerate={() => setShowGenerated(true)}
        onRegenerate={() => setShowGenerated(true)}
        onClose={() => {
          setAssistantOpen(false);
          setShowGenerated(false);
          setAssistantPrompt("");
        }}
      />
      <CreatePolicyDialog
        open={createPolicyOpen}
        onClose={() => setCreatePolicyOpen(false)}
      />
      <EditPolicyDialog
        open={editPolicyOpen}
        policy={selectedPolicy}
        onClose={() => {
          setEditPolicyOpen(false);
          setSelectedPolicy(null);
        }}
      />
      <ConfirmationDialog
        open={deleteOpen}
        title="Delete Policy"
        description={
          policyToDelete
            ? `Are you sure you want to delete "${policyToDelete.name}"? This action cannot be undone.`
            : "Are you sure you want to delete this policy? This action cannot be undone."
        }
        confirmLabel="Delete"
        cancelLabel="Cancel"
        onConfirm={handleDeleteConfirm}
        onClose={handleDeleteClose}
      />
    </Box>
  );
};

export default ZeroTrustPolicies;
